import { useMemo, useRef, useState } from "react";
import { ColDef, ColumnState, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import usePDA1091_R01Store from "./PDA1091_R01Store";
import type { AuditItem } from "./PDA1091_R01Dto";
import {
  aggridHeaderClass,
  pdaCellStyle,
} from "src/components/common/common";
import { usePDA1091_R01Content } from "./PDA1091_R01Content";

type RowType = AuditItem & { inserted?: boolean };

const PDA1091_R01Table = () => {
  const { auditItems } = usePDA1091_R01Store();
  const { handleRemoveFromGrid } = usePDA1091_R01Content();
  const gridRef = useRef<AgGridReact<RowType>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [, setHoveredRowIndex] = useState<number | null>(null);
  const [, setColumnState] = useState<ColumnState[] | null>(null);

  const rowData = useMemo(() => [...auditItems] as RowType[], [auditItems]);

  const columnDefs = useMemo<ColDef<RowType>[]>(() => {
    const left = {
      textAlign: "left",
      ...pdaCellStyle,
    } as const;
    const right = {
      textAlign: "right",
      ...pdaCellStyle,
      justifyContent: "flex-end",
    } as const;
    const center = {
      textAlign: "center",
      ...pdaCellStyle,
      justifyContent: "center",
    } as const;
    return [
      {
        headerName: "품명",
        field: "itemNm",
        valueGetter: (p) => p.data?.itemNm ?? "-",
        cellStyle: left,
        headerClass: aggridHeaderClass,
        flex: 1.8,
        minWidth: 90,
      },
      {
        headerName: "현재고",
        field: "currentStock",
        valueFormatter: (p: ValueFormatterParams) =>
          p.value == null ? "-" : Number(p.value).toLocaleString(),
        cellStyle: right,
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 70,
      },
      {
        headerName: "실재고",
        field: "actualStock",
        valueFormatter: (p: ValueFormatterParams) =>
          p.value == null ? "-" : Number(p.value).toLocaleString(),
        cellStyle: { ...right, fontWeight: "bold" },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 70,
      },
      {
        headerName: "차이",
        field: "diffQty",
        valueFormatter: (p: ValueFormatterParams) => {
          if (p.value == null) return "-";
          const n = Number(p.value);
          return (n > 0 ? "+" : "") + n.toLocaleString();
        },
        cellStyle: (p: { value?: number }) => {
          const n = Number(p.value ?? 0);
          const color = n > 0 ? "#2563eb" : n < 0 ? "#dc2626" : "#9ca3af";
          return { ...right, fontWeight: "bold", color };
        },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 70,
      },
      {
        headerName: "삭제",
        field: "itemCd",
        valueGetter: () => "",
        cellRenderer: (p: { data?: RowType }) => {
          if (!p.data?.itemCd) return "";
          return (
            <button
              type="button"
              onClick={() => handleRemoveFromGrid(p.data!.itemCd)}
              className="text-red-500 font-bold"
              title="삭제"
            >
              ✕
            </button>
          );
        },
        cellStyle: center,
        headerClass: aggridHeaderClass,
        flex: 0.6,
        minWidth: 45,
      },
    ];
  }, [handleRemoveFromGrid]);

  return (
    <div className="flex-1 p-3 pt-2 flex w-full h-full flex-col overflow-hidden">
      <GridContextWrapper
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={rowData}
        initSelected={() => {}}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1091_R01_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<RowType>
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1091_R01_ColumnState"
            setColumnState={setColumnState}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1091_R01Table;
